import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, UserPlus, Phone, KeyRound, Car, Trash2, Save, X, CheckCircle2, XCircle, BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import DriverEarningsModal from "@/components/DriverEarningsModal";
import ExpiryBadges from "@/components/ExpiryBadges";
import { DriverDocumentsSection } from "@/components/admin/DriverDocumentsSection";

const EXPENSE_CATS = [
  { slug: "gorivo",   label: "Gorivo",              emoji: "⛽" },
  { slug: "dijelovi", label: "Dijelovi za vozilo",  emoji: "🔩" },
  { slug: "servis",   label: "Servis vozila",       emoji: "🛠️" },
  { slug: "gume",     label: "Gume",                emoji: "🛞" },
  { slug: "parking",  label: "Parking i cestarine", emoji: "🅿️" },
  { slug: "pranje",   label: "Pranje i održavanje", emoji: "🧼" },
];

const emptyForm = {
  name: "", phone: "", pin: "", driver_type: "employee", vehicle_label: "", active: true,
  fee_model: "flat_5_13", social_fee_weekly_eur: "", iban: "", oib: "", email: "",
  company_name: "", address: "", is_vat_registered: false,
  vehicle_ownership: "own", fuel_refund_pct: "10", expense_refund_pcts: {},
};

export default function AdminDrivers() {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [earningsDriver, setEarningsDriver] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/drivers");
      setDrivers(data);
    } catch (err) {
      if (err?.response?.status === 401) navigate("/admin");
      else toast.error("Failed to load drivers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => { setForm(emptyForm); setEditingId(null); };

  const startEdit = (d) => {
    setEditingId(d.id);
    setForm({
      name: d.name, phone: d.phone, pin: "",
      driver_type: d.driver_type === "subcontractor" ? "subcontractor" : "employee",
      vehicle_label: d.vehicle_label || "", active: d.active,
      fee_model: d.fee_model === "flat_5_625"
        ? "flat_5_20"
        : ["flat_5_20", "flat_5_13", "split_3565"].includes(d.fee_model) ? d.fee_model : "flat_5_13",
      social_fee_weekly_eur: d.social_fee_weekly_eur ?? "",
      iban: d.iban || "", oib: d.oib || "", email: d.email || "",
      company_name: d.company_name || "", address: d.address || "",
      is_vat_registered: !!d.is_vat_registered,
      vehicle_ownership: d.vehicle_ownership || "own",
      fuel_refund_pct: d.fuel_refund_pct ?? "10",
      expense_refund_pcts: d.expense_refund_pcts || {},
    });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return toast.error("Name and phone are required.");
    if (!editingId && !form.pin) return toast.error("PIN is required for new drivers.");
    setBusy(true);
    try {
      const payload = { ...form };
      // Normalise blanks -> null so we don't send empty strings for optional fields.
      if (payload.fee_model === "") delete payload.fee_model;
      if (payload.social_fee_weekly_eur === "") delete payload.social_fee_weekly_eur;
      else payload.social_fee_weekly_eur = Number(payload.social_fee_weekly_eur);
      if (payload.fuel_refund_pct === "" || payload.fuel_refund_pct === null) delete payload.fuel_refund_pct;
      else {
        payload.fuel_refund_pct = Number(payload.fuel_refund_pct);
        if (Number.isNaN(payload.fuel_refund_pct) || payload.fuel_refund_pct < 0 || payload.fuel_refund_pct > 100) {
          setBusy(false);
          return toast.error("Fuel reimbursement % must be between 0 and 100.");
        }
      }
      const pcts = {};
      for (const [slug, val] of Object.entries(payload.expense_refund_pcts || {})) {
        if (val === "" || val === null || val === undefined) continue;
        const n = Number(val);
        if (Number.isNaN(n) || n < 0 || n > 100) {
          setBusy(false);
          return toast.error(`Postotak za "${slug}" mora biti 0-100.`);
        }
        pcts[slug] = n;
      }
      if (payload.fuel_refund_pct !== undefined && pcts.gorivo === undefined) {
        pcts.gorivo = payload.fuel_refund_pct;
      }
      if (Object.keys(pcts).length) payload.expense_refund_pcts = pcts;
      else delete payload.expense_refund_pcts;
      if (editingId) {
        if (!payload.pin) delete payload.pin;
        await api.patch(`/admin/drivers/${editingId}`, payload);
        toast.success("Driver updated.");
      } else {
        await api.post("/admin/drivers", payload);
        toast.success("Driver created.");
      }
      reset();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete driver ${d.name}? Historical bookings will keep their reference.`)) return;
    try {
      await api.delete(`/admin/drivers/${d.id}`);
      toast.success("Driver deleted.");
      if (editingId === d.id) reset();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div data-testid="admin-drivers-page" className="min-h-screen bg-[#050505] text-zinc-100">
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link
            to="/admin/dashboard"
            data-testid="drivers-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-[#C5A059] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </Link>
          <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Drivers</p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-10">
        {/* Form */}
        <div className="h-fit">
        <form
          onSubmit={save}
          data-testid="driver-form"
          className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-6 space-y-4"
        >
          <div className="flex items-center gap-2 text-sm text-white">
            {editingId ? <Save className="w-4 h-4 text-[#C5A059]" /> : <UserPlus className="w-4 h-4 text-[#C5A059]" />}
            <span className="font-medium">{editingId ? "Edit driver" : "Add new driver"}</span>
            {editingId && (
              <button type="button" onClick={reset} className="ml-auto text-zinc-500 hover:text-white" aria-label="Cancel">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <Field label="Full name">
            <input
              data-testid="driver-form-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={FIELD}
              placeholder="Marko Marić"
            />
          </Field>

          <Field label="Phone (with country code)">
            <div className="relative">
              <Phone className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="driver-form-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={`${FIELD} pl-9`}
                placeholder="+385 91 …"
              />
            </div>
          </Field>

          <Field label={`PIN ${editingId ? "(leave blank to keep current)" : "(4–8 digits)"}`}>
            <div className="relative">
              <KeyRound className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="driver-form-pin"
                type="password"
                inputMode="numeric"
                maxLength={8}
                value={form.pin}
                onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/[^0-9]/g, "") })}
                className={`${FIELD} pl-9 tracking-widest`}
                placeholder={editingId ? "••••" : "e.g. 4832"}
              />
            </div>
          </Field>

          <Field label="Driver type">
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "employee", label: "Employee (internal)", sub: "80/20 split · no self-billing" },
                { v: "subcontractor", label: "Subcontractor (own company)", sub: "Self-billing agreement required" },
              ].map((opt) => (
                <button
                  type="button"
                  key={opt.v}
                  onClick={() => setForm({ ...form, driver_type: opt.v })}
                  data-testid={`driver-form-type-${opt.v}`}
                  className={`p-3 rounded-lg text-left border transition-colors ${
                    form.driver_type === opt.v
                      ? "border-[#C5A059] bg-[#C5A059]/10 text-white"
                      : "border-white/10 text-zinc-400 hover:border-white/30"
                  }`}
                >
                  <p className="text-xs font-medium">{opt.label}</p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">{opt.sub}</p>
                </button>
              ))}
            </div>
          </Field>

          <Field label="Fee model (Control Room)">
            <div className="grid grid-cols-1 gap-2">
              {[
                { v: "flat_5_13",  label: "Flat 5% + 13% VAT", sub: "Croatian PT VAT (default 2026) — own vehicle", recommended: true },
                { v: "flat_5_20",  label: "Flat 5% + 20% VAT", sub: "20% + 5% payout tier — own vehicle" },
                { v: "split_3565", label: "35/65 split", sub: "Fleet vehicle · Driver 35% / Company 65%" },
              ].map((opt) => (
                <button
                  type="button"
                  key={opt.v || "none"}
                  onClick={() => setForm({ ...form, fee_model: opt.v })}
                  data-testid={`driver-form-fee-model-${opt.v || "none"}`}
                  className={`p-3 rounded-lg text-left border transition-colors ${
                    form.fee_model === opt.v
                      ? "border-[#C5A059] bg-[#C5A059]/10 text-white"
                      : "border-white/10 text-zinc-400 hover:border-white/30"
                  }`}
                >
                  <p className="text-xs font-medium">{opt.label}</p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">{opt.sub}</p>
                </button>
              ))}
            </div>
          </Field>

          <Field label="Fuel policy (receipts refund)">
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "own", label: "Own vehicle", sub: "Custom % refund below" },
                { v: "company", label: "Company vehicle", sub: "100% fuel refund" },
              ].map((opt) => (
                <button
                  type="button"
                  key={opt.v}
                  onClick={() => setForm({ ...form, vehicle_ownership: opt.v })}
                  data-testid={`driver-form-ownership-${opt.v}`}
                  className={`p-3 rounded-lg text-left border transition-colors ${
                    form.vehicle_ownership === opt.v
                      ? "border-[#C5A059] bg-[#C5A059]/10 text-white"
                      : "border-white/10 text-zinc-400 hover:border-white/30"
                  }`}
                >
                  <p className="text-xs font-medium">{opt.label}</p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">{opt.sub}</p>
                </button>
              ))}
            </div>
          </Field>

          {form.vehicle_ownership !== "company" && (
            <Field label="Refund % per expense category (own vehicle)">
              <div className="grid grid-cols-2 gap-2">
                {EXPENSE_CATS.map((c) => (
                  <div key={c.slug} className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[11px] text-zinc-500 pointer-events-none">
                      {c.emoji} {c.label.split(" ")[0]}
                    </span>
                    <input
                      type="number" min="0" max="100" step="0.5"
                      data-testid={`driver-form-pct-${c.slug}`}
                      value={
                        form.expense_refund_pcts?.[c.slug] ??
                        (c.slug === "gorivo" ? form.fuel_refund_pct : "")
                      }
                      onChange={(e) => setForm({
                        ...form,
                        ...(c.slug === "gorivo" ? { fuel_refund_pct: e.target.value } : {}),
                        expense_refund_pcts: { ...form.expense_refund_pcts, [c.slug]: e.target.value },
                      })}
                      placeholder="zadano"
                      className={`${FIELD} pl-24 pr-7 text-right`}
                    />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 text-xs">%</span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-zinc-600 mt-1">Prazno = zadani postotak iz postavki kategorija. Primjenjuje se na odobrene račune u tjednoj isplati.</p>
            </Field>
          )}

          <Field label="Social fee €/week (optional)">
            <input
              type="number" min="0" step="0.5"
              data-testid="driver-form-social-fee"
              value={form.social_fee_weekly_eur}
              onChange={(e) => setForm({ ...form, social_fee_weekly_eur: e.target.value })}
              className={FIELD}
              placeholder="e.g. 60"
            />
          </Field>

          <Field label="Email (optional)">
            <input
              type="email"
              data-testid="driver-form-email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={FIELD}
              placeholder="driver@example.com"
            />
          </Field>

          <Field label="IBAN (optional, HR format)">
            <input
              data-testid="driver-form-iban"
              value={form.iban}
              onChange={(e) => setForm({ ...form, iban: e.target.value })}
              className={`${FIELD} font-mono text-xs tabular-nums`}
              placeholder="HR1234567890123456789"
            />
          </Field>

          <Field label="OIB (optional, 11 digits)">
            <input
              data-testid="driver-form-oib"
              inputMode="numeric"
              maxLength={11}
              value={form.oib}
              onChange={(e) => setForm({ ...form, oib: e.target.value.replace(/[^0-9]/g, "") })}
              className={`${FIELD} font-mono tracking-widest`}
              placeholder="12345678901"
            />
          </Field>

          <Field label="Company name (subcontractor)">
            <input
              data-testid="driver-form-company-name"
              value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              className={FIELD}
              placeholder="e.g. Prijevoz Marko j.d.o.o."
            />
          </Field>

          <Field label="Company address (subcontractor)">
            <input
              data-testid="driver-form-address"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className={FIELD}
              placeholder="Ulica i broj, mjesto"
            />
          </Field>

          <Field label="VAT status (self-billing)">
            <label className="flex items-center gap-3 h-11 px-3 rounded-lg border border-white/10 bg-black/30 cursor-pointer">
              <input
                type="checkbox"
                data-testid="driver-form-vat-registered"
                checked={!!form.is_vat_registered}
                onChange={(e) => setForm({ ...form, is_vat_registered: e.target.checked })}
                className="accent-[#C5A059]"
              />
              <span className="text-sm text-zinc-300">U sustavu PDV-a (is_vat_registered)</span>
            </label>
          </Field>

          <Field label="Vehicle label (optional)">
            <div className="relative">
              <Car className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="driver-form-vehicle"
                value={form.vehicle_label}
                onChange={(e) => setForm({ ...form, vehicle_label: e.target.value })}
                className={`${FIELD} pl-9`}
                placeholder="Suzuki S-Cross · HR-123-AB"
              />
            </div>
          </Field>

          <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
            <input
              type="checkbox"
              data-testid="driver-form-active"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              className="w-4 h-4 accent-[#C5A059]"
            />
            Active (can log in and receive rides)
          </label>

          <button
            type="submit"
            data-testid="driver-form-save"
            disabled={busy}
            className="w-full h-11 rounded-xl bg-[#C5A059] hover:bg-[#D4AF37] disabled:opacity-60 text-black font-semibold text-sm transition-colors"
          >
            {busy ? "Saving…" : editingId ? "Save changes" : "Create driver"}
          </button>
        </form>

        {editingId && <DriverDocumentsSection driverId={editingId} />}
        </div>

        {/* List */}
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4">
            {loading ? "Loading…" : `${drivers.length} driver${drivers.length === 1 ? "" : "s"}`}
          </p>

          <div className="space-y-3">
            {drivers.map((d) => (
              <div
                key={d.id}
                data-testid={`driver-row-${d.id}`}
                className={`rounded-2xl border ${editingId === d.id ? "border-[#C5A059]/50" : "border-white/10"} bg-[#0d0d0d] p-5 flex items-center justify-between gap-4`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-white font-medium truncate">{d.name}</p>
                    <span data-testid={`driver-type-badge-${d.id}`} className={`text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border ${d.driver_type === "subcontractor" ? "border-purple-500/40 text-purple-300" : "border-[#C5A059]/40 text-[#C5A059]"}`}>
                      {d.driver_type === "subcontractor" ? "Subcontractor" : "Employee"}
                    </span>
                    {d.fee_model && (
                      <span data-testid={`driver-fee-badge-${d.id}`}
                            className="text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border border-purple-500/40 text-purple-300 bg-purple-500/5">
                        {d.fee_model === "flat_5_625" ? "flat 5+6.25%"
                          : d.fee_model === "flat_5_20" ? "flat 5+20%"
                          : d.fee_model === "flat_5_13" ? "flat 5+13%"
                          : "35/65"}
                      </span>
                    )}
                    <span data-testid={`driver-fuel-badge-${d.id}`}
                          className="text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border border-emerald-500/40 text-emerald-300 bg-emerald-500/5">
                      {(d.vehicle_ownership || "own") === "company" ? "fuel 100%" : `fuel ${d.fuel_refund_pct ?? 10}%`}
                    </span>
                    {d.active ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-zinc-600" />
                    )}
                  </div>
                  <p className="text-xs text-zinc-500 mt-1 truncate">
                    {d.phone}{d.vehicle_label ? ` · ${d.vehicle_label}` : ""}
                    {d.email ? ` · ${d.email}` : ""}
                  </p>
                  <ExpiryBadges rec={d} />
                </div>
                <div className="flex items-center gap-2">
                  {d.driver_type !== "subcontractor" && (
                    <button
                      onClick={() => setEarningsDriver(d)}
                      data-testid={`driver-view-rides-${d.id}`}
                      className="text-xs uppercase tracking-[0.2em] px-3 py-2 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300 hover:text-[#C5A059] transition-colors flex items-center gap-1.5"
                    >
                      <BarChart3 className="w-3.5 h-3.5" /> View Rides
                    </button>
                  )}
                  <button
                    onClick={() => startEdit(d)}
                    data-testid={`driver-edit-${d.id}`}
                    className="text-xs uppercase tracking-[0.2em] px-3 py-2 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300 hover:text-[#C5A059] transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => remove(d)}
                    data-testid={`driver-delete-${d.id}`}
                    className="p-2 rounded-full text-zinc-500 hover:text-red-400 transition-colors"
                    aria-label="Delete driver"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {!loading && drivers.length === 0 && (
              <p className="text-sm text-zinc-500">No drivers yet. Create your first driver on the left.</p>
            )}
          </div>
        </div>
      </main>
      {earningsDriver && (
        <DriverEarningsModal driver={earningsDriver} onClose={() => setEarningsDriver(null)} />
      )}
    </div>
  );
}

const FIELD = "w-full h-11 pl-3 pr-3 bg-[#050505] border border-white/10 rounded-lg text-sm text-white placeholder:text-zinc-600 focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/40 outline-none transition-colors";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
